import React, { useMemo } from 'react';
import AdminList, {
	NameCellRenderer,
	ArticleCountCellRenderer,
	ArticleViewCellRenderer,
	ReadOnlyLabelCellRenderer
} from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { Desk } from '../../../shared/types';
import { makeStyles, createStyles } from '@material-ui/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DESKS_COUNTS, DESKS_SHORT } from '../../../gql/desks';
import { ValueFormatterParams } from 'ag-grid-community/dist/lib/entities/colDef';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';
import { sortArray, filterArrayOfObjectsBy } from '../../../utils/sorting.utils';
import { isChannelAdmin } from '../../../utils/channel.utils';
import { ResearchRole } from '../../../shared/global-enums';
import { useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { config } from '../../../config';
import { useUser } from '../../providers/UserProvider';

const name = 'Desks';
const apiName = name.toLowerCase();

const useStyles = makeStyles(() => createStyles({
	gridHeader: {
		fontWeight: 'bold'
	}
}));

const DeskList = () => {
	const { hasScope } = useUser();
	const isAdmin = [ResearchRole.Administrator].some(hasScope);
	const { accessToken } = useAuth0Data();
	const neonUserId = jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];
	const { loading, error, data } = useQuery(DESKS_SHORT, { fetchPolicy: 'network-only' });
	const { data: countsData } = useQuery(DESKS_COUNTS, { fetchPolicy: 'network-only' });
	const desks: Desk[] = data?.documents ?? [];
	const classes = useStyles();
	const articleFilter = useArticleFilter();
	const countsMap: any = useMemo(() => countsData ? (countsData.documents as Desk[]).reduce((t, d) => ({...t, [d._id]: {...d}}), {}) : {}, [countsData]);
	const sortedRecords = useMemo(() => {
		const desksData = desks.map(d => ({...d, ...(countsMap[d._id] ? countsMap[d._id] : {})}));
		return sortArray(filterArrayOfObjectsBy(desksData, '', ['name', 'description']), 'name');
	}, [desks, countsMap]);

	const columnDefs: ColDef[] = [
		{
			colId: 'desk-name-col',
			headerName: 'Name',
			headerClass: classes.gridHeader,
			field: 'name',
			cellRendererFramework: (props: any) => isAdmin || isChannelAdmin(props.data.contributors, neonUserId) ?
				<NameCellRenderer {...props} apiName={name} /> :
				<ReadOnlyLabelCellRenderer {...props} />,
			width: 260,
			menuTabs: []
		},
		{
			colId: 'desk-email-col',
			headerName: 'Email',
			headerClass: classes.gridHeader,
			field: 'email',
			width: 260,
			menuTabs: []
		},
		{
			colId: 'desk-members-col',
			headerName: 'Members',
			headerClass: classes.gridHeader,
			field: 'contributors',
			valueFormatter: (params: ValueFormatterParams) =>{
				const contributorsCount: number = params.value?.length;
				return `${contributorsCount} contributor${contributorsCount > 1 ? 's' : ''}`;
			},
			width: 160,
			menuTabs: []
		},
		{
			colId: 'desk-articles-col',
			headerName: 'Articles',
			headerClass: classes.gridHeader,
			field: 'articleCount',
			cellRendererFramework: (props: ICellRendererParams) => <ArticleCountCellRenderer value={props.value} filter={articleFilter} context={props.context} filterData={{_id: props.data._id, name: props.data.name, type: 'desk'}} />,
			width: 160,
			menuTabs: []
		},
		{
			headerName: 'Views',
			field: 'views',
			width: 160,
			cellRendererFramework: (props: any) => <ArticleViewCellRenderer {...props} apiName={apiName} filter={articleFilter} linkTo="" />,
			menuTabs: []
		}

	];

	return (
		<AdminList
			name={name}
			items={sortedRecords}
			loading={loading}
			error={error}
			columns={columnDefs}
			showAdd={true}
		/>
	);
};

export default DeskList;
